import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { Grid, FormControlLabel, Switch } from '@mui/material';
import { Button, Typography, Autocomplete, DynamicField } from '@totalsoft/rocket-ui';
import debounce from 'debounce-promise';
import memoize from 'lodash.memoize';
import { withRights } from 'providers/RightsConsumer';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import { getDynamicFieldControlType } from 'utils/functions';
import OfferPaymentPlanParamsCalendarView from './OfferPaymentPlanParamsCalendarView';
import OfferPaymentPlanParamsListView from './OfferPaymentPlanParamsListView';

const fixedTableColumns = [
  { cellParamId: -3, cellParamName: 'Selected', controlType: 'BOOL', readOnly: false },
  { cellParamId: -2, cellParamName: 'MonthNo', controlType: 'TP_NUM', readOnly: true },
  { cellParamId: -1, cellParamName: 'MonthDate', controlType: 'DATE', readOnly: true }
];

export const OfferPaymentPlanParamsComponent = ({
  t,
  scheduleCellParamHeader,
  scheduleCellParamColumns,
  scheduleCellParamValues,
  handleHeaderPropertyChange,
  handleRowPropertyChange,
  handlePropertyParamChange,
  handleRowsPropertyChange,
  scheduleTypeCellParamTemplates,
  selectedScheduleTypeCellParamTemplate,
  handleScheduleTypeCellParamTemplateChange,
  onSave,
  onApply,
  onReset,
  saving,
  isDirty,
  isTemplateChanged,
  isContractModification,
  handleMonthClick,
  handleYearClick,
  handleAllMonthsSelection,
  selectedOptions,
  setSelectedOptions,
  onResetSelectedCells,
  isCalendarView,
  handleCalendarViewChange,
  rights
}) => {
  const getDynamicInputParams = memoize(paramTypeName => {
    switch (paramTypeName) {
      case 'PERCENT':
        return { controlType: 'TP_NUM', inputProps: { decimalScale: 4, thousandseparator: '' } };
      case 'PERCENT6':
        return { controlType: 'TP_NUM', inputProps: { decimalScale: 6, thousandseparator: '' } };
      case 'DATE':
        return { controlType: 'TP_DATE' };
      case 'AMOUNT':
        return { controlType: 'AMOUNT', inputProps: { decimalScale: 2, thousandseparator: '' } };
      case 'BOOL':
        return { controlType: 'BOOL' };
      default:
        return { controlType: 'TP_TEXT' };
    }
  });

  return (
    <>
      {<Prompt when={isDirty === true} message={t('LeavingWithoutSaving')} />}
      <Grid container spacing={3} alignItems={'flex-end'}>
        <Grid item xs={12} sm={6} lg={3}>
          <Autocomplete
            id={'offerPaymentPlanParamsCard_cellParamTemplates'}
            fullWidth
            value={selectedScheduleTypeCellParamTemplate ? selectedScheduleTypeCellParamTemplate : ''}
            valueKey='scheduleTypeCellParamTemplateId'
            labelKey='templateName'
            label={t('OfferEdit.PaymentPlan.ScheduleTypeCellParamTemplate')}
            options={scheduleTypeCellParamTemplates}
            onChange={handleScheduleTypeCellParamTemplateChange}
            isClearable={false}
            disabled={rights.readOnlyFull || !rights.seasonalSettingsWrite}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          {!isCalendarView && (
            <Autocomplete
              id={'offerPaymentPlanParamsCard_cellParamDisplayName'}
              fullWidth
              value={scheduleCellParamHeader.cellParamId}
              valueKey='cellParamId'
              labelKey='cellParamDisplayName'
              label={t('OfferEdit.PaymentPlan.ScheduleTypeCellParam')}
              options={scheduleCellParamColumns}
              onChange={debounce(handleHeaderPropertyChange('cellParamId'), 200)}
              isClearable={false}
              simpleValue
              disabled={
                rights.readOnlyFull ||
                (!isContractModification && selectedScheduleTypeCellParamTemplate
                  ? !selectedScheduleTypeCellParamTemplate.isEditable
                  : false) ||
                !rights.seasonalSettingsWrite
              }
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          {!isCalendarView && scheduleCellParamHeader.controlType ? (
            <DynamicField
              id='cellParamIdInput'
              controlType={getDynamicFieldControlType(scheduleCellParamHeader.controlType)}
              label={scheduleCellParamHeader.controlType === 'BOOL' ? '' : t('OfferEdit.PaymentPlan.ScheduleTypeCellParamValue')}
              value={scheduleCellParamHeader.paramValue}
              onChange={debounce(handleHeaderPropertyChange('paramValue'), 200)}
              disabled={rights.readOnlyFull || !rights.seasonalSettingsWrite}
            />
          ) : null}
        </Grid>

        <GridActionsWrapper item xs={12} sm={6} lg={3} alignItems='center' justifyContent='right'>
          {!isCalendarView && (
            <>
              <Button
                id={'offerPaymentPlanParamsCard_applyBtn'}
                color='primary'
                variant='contained'
                disabled={
                  rights.readOnlyFull ||
                  isTemplateChanged ||
                  (!isContractModification && selectedScheduleTypeCellParamTemplate
                    ? !selectedScheduleTypeCellParamTemplate.isEditable
                    : false) ||
                  !rights.seasonalSettingsWrite
                }
                onClick={onApply}
              >
                {t('Apply')}
              </Button>
              <Button
                id={'offerPaymentPlanParamsCard_clearAllBtn'}
                color='primary'
                variant='outlined'
                onClick={onReset}
                disabled={
                  rights.readOnlyFull ||
                  isTemplateChanged ||
                  (!isContractModification && selectedScheduleTypeCellParamTemplate
                    ? !selectedScheduleTypeCellParamTemplate.isEditable
                    : false) ||
                  !rights.seasonalSettingsWrite
                }
              >
                {t('OfferEdit.PaymentPlan.ClearAll')}
              </Button>
            </>
          )}
        </GridActionsWrapper>

        {scheduleCellParamHeader.cellParamId ? <Grid item xs={12} md={6} lg={3} /> : <Grid item xs={12} md={6} />}
        <Grid item container xs={12}>
          <Grid item xs>
            {isTemplateChanged && <Typography>{t('OfferEdit.PaymentPlan.PreviewingTemplateChange')}</Typography>}
          </Grid>
          <Grid item container xs={12} sm={6} lg={3} alignItems='center' justifyContent='right'>
            <FormControlLabel
              width='100%'
              control={<Switch checked={isCalendarView} onChange={handleCalendarViewChange} />}
              label={t('OfferEdit.PaymentPlan.CalendarView')}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container mt={3}>
        <Grid item xs={12}>
          {isCalendarView ? (
            <OfferPaymentPlanParamsCalendarView
              fixedTableColumns={fixedTableColumns}
              scheduleCellParamColumns={scheduleCellParamColumns}
              scheduleCellParamValues={scheduleCellParamValues}
              handleRowPropertyChange={handleRowPropertyChange}
              handleRowsPropertyChange={handleRowsPropertyChange}
              getDynamicInputParams={getDynamicInputParams}
              onReset={onReset}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              handleMonthClick={handleMonthClick}
              handleYearClick={handleYearClick}
              handleAllMonthsSelection={handleAllMonthsSelection}
              onResetSelectedCells={onResetSelectedCells}
              isContractModification={isContractModification}
              selectedScheduleTypeCellParamTemplate={selectedScheduleTypeCellParamTemplate}
              isEditable={
                !rights.readOnlyFull &&
                !isTemplateChanged &&
                (!isContractModification && selectedScheduleTypeCellParamTemplate
                  ? selectedScheduleTypeCellParamTemplate.isEditable
                  : true) &&
                rights.seasonalSettingsWrite &&
                !saving
              }
            />
          ) : (
            <OfferPaymentPlanParamsListView
              fixedTableColumns={fixedTableColumns}
              scheduleCellParamColumns={scheduleCellParamColumns}
              scheduleCellParamValues={scheduleCellParamValues}
              handleRowPropertyChange={handleRowPropertyChange}
              handlePropertyParamChange={handlePropertyParamChange}
              isContractModification={isContractModification}
              selectedScheduleTypeCellParamTemplate={selectedScheduleTypeCellParamTemplate}
              getDynamicInputParams={getDynamicInputParams}
              isEditable={
                !rights.readOnlyFull &&
                !isTemplateChanged &&
                (!isContractModification && selectedScheduleTypeCellParamTemplate
                  ? selectedScheduleTypeCellParamTemplate.isEditable
                  : true) &&
                rights.seasonalSettingsWrite &&
                !saving
              }
            />
          )}
        </Grid>

        <GridActionsWrapper item xs={12}>
          <Button
            id={'offerPaymentPlanParamsCard_saveBtn'}
            onClick={() => onSave(scheduleCellParamValues)}
            style={{ marginTop: 40 }}
            disabled={rights.readOnlyFull || saving || !isDirty || !rights.seasonalSettingsWrite}
          >
            {saving ? t('Saving') : t('Save')}
          </Button>
        </GridActionsWrapper>
      </Grid>
    </>
  );
};

OfferPaymentPlanParamsComponent.propTypes = {
  t: PropTypes.func.isRequired,
  scheduleCellParamHeader: PropTypes.object.isRequired,
  scheduleCellParamValues: PropTypes.array,
  scheduleCellParamColumns: PropTypes.array,
  handleHeaderPropertyChange: PropTypes.func.isRequired,
  handleRowPropertyChange: PropTypes.func.isRequired,
  handlePropertyParamChange: PropTypes.func.isRequired,
  handleRowsPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  isTemplateChanged: PropTypes.bool.isRequired,
  scheduleTypeCellParamTemplates: PropTypes.array,
  selectedScheduleTypeCellParamTemplate: PropTypes.object,
  handleScheduleTypeCellParamTemplateChange: PropTypes.func.isRequired,
  isContractModification: PropTypes.bool.isRequired,
  handleMonthClick: PropTypes.func.isRequired,
  handleYearClick: PropTypes.func.isRequired,
  handleAllMonthsSelection: PropTypes.func.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  onResetSelectedCells: PropTypes.func.isRequired,
  isCalendarView: PropTypes.bool.isRequired,
  handleCalendarViewChange: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired
};

export default compose(withTranslation())(withRights(OfferPaymentPlanParamsComponent));
